import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { radiusTestHeadline, testRadiusLogin, type RadiusTestResult } from "../../api/radiusTest";
import type { InvoicePrefSubscriber } from "../../api/invoicePrefs";
import { PasswordField } from "../../components/PasswordField";

/**
 * Tools › Test RADIUS login (owner 2026-10-21): type the username and the
 * password the subscriber gives you; see what RADIUS answers and why. The
 * password box is cleared after every test and never kept.
 */
export function SubscriberRadiusTestDialog({ ns, subscriber, onClose }: { ns: string; subscriber: InvoicePrefSubscriber | null; onClose: () => void }) {
  const identity = useIdentity();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const test = useMutation({
    mutationFn: (v: { username: string; password: string }) => testRadiusLogin(identity, ns, subscriber!.accountid, v.username, v.password),
  });
  const { reset } = test;
  useEffect(() => {
    setUsername(subscriber?.username ?? "");
    setPassword("");
    reset();
  }, [subscriber, reset]);
  if (!subscriber) return null;
  const run = () => {
    const v = { username: username.trim(), password };
    setPassword("");
    test.mutate(v);
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Test RADIUS login · {subscriber.name || subscriber.mobile || subscriber.username}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Ask the subscriber for the password they are using and type it here. It goes straight to RADIUS and is not kept. Up to 5 tests per subscriber an hour;
          each test is recorded under your name.
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField size="small" label="Username" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" />
          <PasswordField
            size="small"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            onKeyDown={(e) => {
              if (e.key === "Enter" && username.trim() && password && !test.isPending) run();
            }}
          />
        </Box>
        {test.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {test.error instanceof Error ? test.error.message : String(test.error)}
          </Alert>
        )}
        {test.data && <RadiusTestView r={test.data} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" disabled={!username.trim() || !password || test.isPending} onClick={run}>
          {test.isPending ? "Testing…" : "Test"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** The answer without the dialog's hooks, so a test can render it. */
export function RadiusTestView({ r }: { r: RadiusTestResult }) {
  const head = radiusTestHeadline(r);
  return (
    <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
      <Alert severity={head.severity}>{head.text}</Alert>
      {r.reply_message && (
        <Typography variant="body2">
          RADIUS says: <strong>{r.reply_message}</strong>
        </Typography>
      )}
      {r.checks.map((c) => (
        <Box key={c.label} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Chip size="small" color={c.ok ? "success" : "error"} label={c.ok ? "OK" : "Problem"} />
          <Typography variant="body2">
            <strong>{c.label}:</strong> {c.detail}
          </Typography>
        </Box>
      ))}
      {r.attributes.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          Plan sent to the router: {r.attributes.join(" · ")}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        Tests left for this subscriber this hour: {r.tests_left}
      </Typography>
    </Box>
  );
}
